'use client';

import { useMemo, useState } from 'react';
import { Button, DatePicker, Flex, Modal, Typography, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { BareLogPreviewItem } from '@evimo/types/api/goldenRecord/export-bare-logs/export-bare-logs.response.dto';
import { AssetLogsService } from '~/app/services/goldenRecord/asset-logs.service';
import { JsonView } from '~/app/components/common/JsonView/JsonView';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Text } = Typography;

const PAGE_SIZE = 20;

type Props = {
  organizationId: string;
  assetId: string;
  open: boolean;
  initialFrom?: string;
  initialTo?: string;
  onClose: () => void;
};

function formatPreview(value: unknown): string {
  if (value == null) return '—';
  const text =
    typeof value === 'string' ? value : JSON.stringify(value, null, 0);
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
}

function BareLogExpandedContent({ value }: { value: unknown }) {
  return (
    <div style={{ maxHeight: 360, overflow: 'auto', padding: '4px 8px' }}>
      <JsonView value={value} wrapInCard={false} maxHeight={320} />
    </div>
  );
}

export function AssetBareLogsPreviewModal({
  organizationId,
  assetId,
  open,
  initialFrom,
  initialTo,
  onClose,
}: Props) {
  const { t } = useTranslation('assets');
  const [page, setPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >([
    initialFrom ? dayjs(initialFrom) : dayjs().subtract(1, 'month'),
    initialTo ? dayjs(initialTo) : dayjs(),
  ]);

  const from = dateRange?.[0]?.toISOString();
  const to = dateRange?.[1]?.toISOString();
  const canQuery = open && !!organizationId && !!from && !!to;

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['asset-bare-logs', organizationId, assetId, from, to, page],
    queryFn: () =>
      AssetLogsService.previewBareLogs(organizationId, assetId, {
        from: from!,
        to: to!,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: canQuery,
  });

  const columns: ColumnsType<BareLogPreviewItem> = useMemo(
    () => [
      {
        title: t('columns.logId'),
        dataIndex: 'logId',
        key: 'logId',
        width: 180,
        ellipsis: true,
      },
      {
        title: t('columns.messageType'),
        dataIndex: 'messageType',
        key: 'messageType',
        width: 140,
        ellipsis: true,
      },
      {
        title: t('columns.receivedAt'),
        key: 'receivedAt',
        width: 180,
        render: (_, record) =>
          dayjs(record.receivedAt ?? record.createdAt).format(
            'YYYY-MM-DD HH:mm:ss',
          ),
      },
      {
        title: t('columns.rawLog'),
        key: 'rawMessage',
        ellipsis: true,
        render: (_, record) => (
          <Text type="secondary" style={{ fontFamily: 'monospace' }}>
            {formatPreview(record.rawMessage)}
          </Text>
        ),
      },
    ],
    [t],
  );

  const handleDownload = async () => {
    if (!from || !to) return;

    setIsDownloading(true);
    try {
      await AssetLogsService.downloadBareLogs(organizationId, assetId, {
        from,
        to,
      });
      message.success(t('detail.logs.actions.downloadBareLogsSuccess'));
    } catch (downloadError) {
      message.error(
        downloadError instanceof Error
          ? downloadError.message
          : t('detail.logs.actions.downloadBareLogsError'),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('detail.logs.actions.previewBareLogs')}
      onCancel={onClose}
      width={960}
      destroyOnHidden
      footer={[
        <Button key="close" onClick={onClose}>
          {t('detail.logs.actions.closePreview')}
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          loading={isDownloading}
          disabled={!from || !to}
          onClick={handleDownload}
        >
          {t('detail.logs.actions.downloadBareLogsConfirm')}
        </Button>,
      ]}
    >
      <Flex vertical gap={16}>
        <Text type="secondary">
          {t('detail.logs.downloadDialog.description')}
        </Text>
        <DatePicker.RangePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates as [Dayjs, Dayjs]);
            setPage(1);
            setExpandedRowKeys([]);
          }}
          style={{ width: '100%', maxWidth: 480 }}
        />
        {isError ? (
          <Text type="danger">
            {error instanceof Error
              ? error.message
              : t('detail.logs.actions.previewBareLogsError')}
          </Text>
        ) : null}
        <EvimoTable<BareLogPreviewItem>
          rowKey="logId"
          loading={isFetching}
          columns={columns}
          dataSource={data?.items ?? []}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) =>
              setExpandedRowKeys(keys as string[]),
            expandedRowRender: (record) => (
              <BareLogExpandedContent value={record.rawMessage} />
            ),
            rowExpandable: (record) => record.rawMessage != null,
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.total ?? 0,
            showSizeChanger: false,
            onChange: (nextPage) => {
              setPage(nextPage);
              setExpandedRowKeys([]);
            },
          }}
          locale={{
            emptyText: canQuery
              ? t('noLogs')
              : t('detail.logs.downloadDialog.description'),
          }}
        />
        <Button
          type="link"
          onClick={() => refetch()}
          disabled={!canQuery}
          style={{ alignSelf: 'flex-start', paddingInline: 0 }}
        >
          {t('overview.refresh', 'Refresh')}
        </Button>
      </Flex>
    </Modal>
  );
}
